<template>
  <v-row justify="center">
    <v-col cols="12" style="max-width:680px">
      <v-sheet class="pt-6">
        <h1 class="display-2 font-weight-bold d-inline-block mr-4"
          >Getting Started with Minimart</h1
        >
        <Markdown
          v-if="getShopDetail('FAQ Intro').length"
          class="title my-4"
          :source="getShopDetail('FAQ Intro')"
        />
        <v-row wrap class="faqs">
          <v-col class="pb-7">
            <h3 class="headline">Table of Contents</h3>
            <v-divider></v-divider>
            <ul v-if="topics" v-intersect="onIntersect">
              <li
                v-for="(topicName, i) of topics"
                :key="topicName"
                class="mt-6"
              >
                <h2
                  class="toc-topic display-1 font-weight-bold"
                  @mouseover="hoveringTopic = topicName"
                  @mouseleave="hoveringTopic = null"
                  ><a v-scroll-to="`#${slugMe(topicName)}`">
                    {{ i + 1 }}. {{ topicName }} </a
                  ><v-btn
                    small
                    icon
                    v-if="hoveringTopic === topicName"
                    @click="copyLink(`#${slugMe(topicName)}`)"
                    ><v-icon small>
                      mdi-link
                    </v-icon></v-btn
                  ></h2
                >
                <ul v-if="faqByTopic[topicName]">
                  <li
                    v-for="faq of faqByTopic[topicName]"
                    :key="faq.Question"
                    class="toc-question mt-2 title"
                    @mouseover.stop="hoveringTopic = faq.Question"
                    @mouseleave.stop="hoveringTopic = null"
                    ><a
                      v-scroll-to="{
                        el: `#${slugMe(faq.Question)}`,
                        offset: -105,
                      }"
                    >
                      <v-icon small color="blue darken-1" class="mb-1"
                        >mdi-arrow-right-drop-circle-outline</v-icon
                      >
                      {{ faq.Question }} </a
                    ><v-btn
                      small
                      icon
                      v-if="hoveringTopic === faq.Question"
                      @click="copyLink(`#${slugMe(faq.Question)}`)"
                      ><v-icon small>
                        mdi-link
                      </v-icon></v-btn
                    ></li
                  >
                </ul>
              </li>
            </ul>
            <v-divider class="mt-6" />
            <v-row wrap v-if="topics" class="py-4">
              <v-col
                cols="12"
                v-for="(topicName, i) of topics"
                :key="topicName"
              >
                <div class="topic-name py-1">
                  <h2 :id="slugMe(topicName)" class="headline mb-2"
                    >{{ i + 1 }}. {{ topicName }}</h2
                  >
                  <v-divider></v-divider>
                </div>
                <ul v-if="faqByTopic[topicName]">
                  <li
                    v-for="faq of faqByTopic[topicName]"
                    :key="faq.Question"
                    :id="`${slugMe(faq.Question)}`"
                    class="py-2 faq-wrap"
                  >
                    <!-- <v-icon left color="blue lighten-2" class="link-icon"
                        >mdi-link-variant</v-icon
                      > -->
                    <v-row class="faq-question mb-2" dense>
                      <v-col cols="12" sm="auto" class="grow"
                        ><h3 class="font-weight-bold headline">
                          {{ faq.Question }}</h3
                        ></v-col
                      >
                      <v-col
                        cols="12"
                        sm="auto"
                        class="shrink"
                        align-self="center"
                        ><v-btn
                          v-if="faq['Video URL']"
                          :href="faq['Video URL']"
                          target="_blank"
                          :x-small="$mq === 'xs'"
                          :small="$mq !== 'xs'"
                          outlined
                          rounded
                          color="teal accent-4"
                          >{{
                            ['xs', 'sm', 'md'].includes($mq)
                              ? 'Watch'
                              : 'Watch Video'
                          }}
                          <v-icon
                            right
                            :x-small="$mq === 'xs'"
                            :small="$mq !== 'xs'"
                            >mdi-play-circle-outline</v-icon
                          ></v-btn
                        ></v-col
                      >
                    </v-row>

                    <Markdown
                      v-if="faq.Answer.length"
                      class="title"
                      :source="faq.Answer"
                    />
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto">
            <v-btn dark depressed color="blue darken-1" to="/signup"
              >Signup for a Shop</v-btn
            >
          </v-col>
          <v-col cols="auto">
            <v-btn dark outlined color="blue darken-1" to="/"
              >Back to the Home Page</v-btn
            ></v-col
          >
        </v-row>
        <v-divider class="my-4" />
        <v-row>
          <v-col cols="auto">
            <h2 class="display-1 font-weight-bold">Still Stumped?</h2>
            <p class="title"
              >Send an email to
              <a href="mailto:hi@getminimart.com">hi@getminimart.com</a>
              and we'll do our best to answer your questions quickly. Or if
              you'd rather join our office hours on Monday and Thursday, find a
              time on our calendar
              <a href="https://calendly.com/getresto/minimart-office-hours"
                >here</a
              >.</p
            >
          </v-col>
        </v-row>
      </v-sheet>
    </v-col>
    <v-fab-transition>
      <v-btn
        v-if="showFAB"
        color="deep-orange"
        dark
        fixed
        bottom
        right
        fab
        v-scroll-to="'body'"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-snackbar
      v-model="showLinkCopiedMessage"
      timeout="-1"
      color="blue darken-1"
    >
      <h3 class="text-center">
        Link Copied to the Clipboard
      </h3>
    </v-snackbar>
  </v-row>
</template>

<script>
import Markdown from '@/components/Markdown'
import { mapGetters, mapActions } from 'vuex'
import _groupBy from 'lodash/groupBy'
import slugify from 'slugify'

export default {
  name: 'FAQView',
  meta() {
    return {
      title: 'Minimart | FAQ - Answers to common questions.',
      meta: [
        {
          name: 'description',
          content:
            'Find answers quickly and get back to running your business.',
        },
      ],
    }
  },
  components: { Markdown },
  computed: {
    ...mapGetters('shop', ['getFAQs', 'getShopDetail']),
    faqByTopic() {
      return _groupBy(this.getFAQs, 'Topic')
    },
    topics() {
      return Object.keys(this.faqByTopic)
    }, // topics
  },
  data() {
    return {
      showFAB: false,
      hoveringTopic: null,
      canCopy: false,
      showLinkCopiedMessage: false,
    }
  },
  methods: {
    ...mapActions('shop', ['fetchFAQs']),
    onIntersect(entries) {
      // More information about these options
      // is located here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      this.showFAB = !entries[0].isIntersecting
    },
    slugMe(string) {
      return slugify(string, { lower: true, remove: /[*+~.&()'"!:@?\\/]/g })
    },
    async copyLink(hash) {
      let link = `${document.location.origin}${this.$route.path}${hash}`
      await navigator.clipboard.writeText(link)
      this.showLinkCopiedMessage = true
      setTimeout(() => {
        this.showLinkCopiedMessage = false
      }, 2000)
    },
  },
  async created() {
    this.canCopy = !!navigator.clipboard
    await this.fetchFAQs()
    if (this.$route.hash) {
      this.$scrollTo(this.$route.hash, 700, { offset: -105 })
    }
  },
}
</script>

<style lang="scss">
.faqs {
  ul {
    list-style-type: none;
    padding-left: 0;
    li {
      padding-left: 0px;
      a {
        text-decoration: none;
      }
    }
  }
  .topic-name {
    position: sticky;
    top: 56px;
    background: white;
    z-index: 3;
  }
  .faq-wrap {
    position: relative;
    .faq-question {
      position: sticky;
      top: 105px;
      background: white;
      z-index: 2;
    }
    .link-icon {
      position: absolute;
      top: 0.5em;
      left: -4px;
      transform: translateX(-100%);
    }
  }
}
</style>
